/**
 * Live processing checklist: Uploading -> Extracting Frames -> Extracting
 * Audio -> Transcribing -> Claude Frame Analysis -> Generating Final Summary.
 */
import React from 'react';

const STEP_LABELS = {
  uploading: 'Uploading',
  extracting_frames: 'Extracting Frames',
  extracting_audio: 'Extracting Audio',
  transcribing: 'Transcribing',
  analyzing_frames: 'Visual Frame Analysis',
  generating_summary: 'Generating Final Summary',
  completed: 'Completed',
};

const STEP_ORDER = [
  'uploading',
  'extracting_frames',
  'extracting_audio',
  'transcribing',
  'analyzing_frames',
  'generating_summary',
];

function StepIcon({ status, index }) {
  if (status === 'completed') {
    return (
      <span className="w-8 h-8 rounded-full bg-emerald-500 text-white grid place-items-center text-xs shrink-0 z-10">
        <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4">
          <path d="m4 10.5 3.5 3.5L16 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="w-8 h-8 rounded-full bg-brand-600 text-white grid place-items-center shrink-0 z-10 relative">
        <span className="absolute inset-0 rounded-full border-2 border-brand-300 border-t-transparent animate-spin" />
        <span className="text-xs font-semibold">{index + 1}</span>
      </span>
    );
  }
  return (
    <span className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 border border-gray-200 grid place-items-center text-xs font-medium shrink-0 z-10">
      {index + 1}
    </span>
  );
}

export default function ProgressSteps({ steps = {}, overallProgress = 0, error }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6 sm:p-8">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Processing Video</h2>
          <p className="text-sm text-gray-400 mt-0.5">This can take a few minutes depending on video length.</p>
        </div>
        <span className="text-3xl font-bold text-brand-600 tabular-nums">{overallProgress}%</span>
      </div>

      <div className="w-full bg-gray-100 rounded-full h-1.5 my-6 overflow-hidden">
        <div
          className="bg-brand-600 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${overallProgress}%` }}
        />
      </div>

      <ul className="relative">
        {STEP_ORDER.map((step, idx) => {
          const info = steps[step] || { status: 'pending', progress: 0 };
          const isLast = idx === STEP_ORDER.length - 1;
          return (
            <li key={step} className="relative flex gap-4 pb-7 last:pb-0">
              {!isLast && (
                <span
                  className={`absolute left-4 top-8 -translate-x-1/2 w-px h-[calc(100%-1.5rem)] ${
                    info.status === 'completed' ? 'bg-emerald-300' : 'bg-gray-200'
                  }`}
                />
              )}
              <StepIcon status={info.status} index={idx} />
              <div className="flex-1 flex items-center justify-between pt-1">
                <span
                  className={`text-sm ${
                    info.status === 'completed'
                      ? 'text-gray-700 font-medium'
                      : info.status === 'in_progress'
                      ? 'text-brand-700 font-semibold'
                      : 'text-gray-400'
                  }`}
                >
                  {STEP_LABELS[step]}
                </span>
                {info.status === 'in_progress' && (
                  <span className="text-xs font-medium text-brand-500">{info.progress}%</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
          {error}
        </div>
      )}
    </div>
  );
}
